import { gql, useMutation } from "@apollo/client";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";

import { useErrandLocationsQuery } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import ConfigureErrandType from "./ConfigureErrandType";
import ErrandLocationForm from "./ErrandLocationForm";
import ErrandProviderRequirementForm from "./ErrandProviderRequirementForm";

type Props = {
  errandTypeId: number;
};

const ErrandType = ({ errandTypeId }: Props) => {
  const { errandTypes } = useOrgConfig();
  const errandType = errandTypes[errandTypeId];
  const { data: locationsData, error: locationsError, refetch: refetchLocations } = useErrandLocationsQuery({
    variables: { errandTypeId },
    skip: !errandType,
  });
  const [addingLocation, setAddingLocation] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: number; anchor: HTMLElement } | null>(null);
  const [deleteLocation, { loading: deleteLoading }] = useMutation(DELETE_ERRAND_LOCATION);

  if (!errandType) return null;

  const locations = locationsData?.errandLocation ?? [];
  const organizationalUnits = locationsData?.organizationalUnit ?? [];

  const onRemove = async () => {
    if (!removeTarget) return;
    await deleteLocation({
      variables: { id: removeTarget.id },
      awaitRefetchQueries: true,
      refetchQueries: ["ErrandLocations"],
    });
    setRemoveTarget(null);
  };

  return (
    <Grid container spacing={2} alignItems="stretch">
      <Grid size={{ xs: 12, xl: 6 }}>
        <ConfigureErrandType errandType={errandType} />
      </Grid>
      <Grid size={{ xs: 12, xl: 6 }}>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Typography>Providers</Typography>
            <Divider sx={{ my: 1 }} />
            <ErrandProviderRequirementForm errandType={errandType} />
          </CardContent>
        </Card>
      </Grid>
      <Grid size={12}>
        <Card>
          <CardContent>
            <Typography>Locations</Typography>
            <Divider sx={{ my: 1 }} />
            {locationsError && (
              <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => void refetchLocations()}>Retry</Button>}>
                Unable to load errand locations.
              </Alert>
            )}
            {locationsData && locations.length === 0 && !addingLocation ? (
              <Typography color="text.secondary" sx={{ my: 2 }}>No Locations Configured</Typography>
            ) : null}
            {locations.map(location => (
              <Accordion key={location.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flexGrow: 1 }}>
                    {location.name}
                  </Typography>
                  <IconButton
                    component="span"
                    aria-label={`Remove ${location.name}`}
                    size="small"
                    onClick={event => {
                      event.stopPropagation();
                      setRemoveTarget({ id: location.id, anchor: event.currentTarget });
                    }}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails>
                  <ErrandLocationForm errandTypeId={errandType.id} location={location} organizationalUnits={organizationalUnits} />
                </AccordionDetails>
              </Accordion>
            ))}
            {addingLocation ? (
              <Accordion expanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>New Location</AccordionSummary>
                <AccordionDetails>
                  {locationsData ? (
                    <ErrandLocationForm
                      errandTypeId={errandType.id}
                      organizationalUnits={organizationalUnits}
                      onCancelDraft={() => setAddingLocation(false)}
                    />
                  ) : (
                    <Button onClick={() => setAddingLocation(false)}>Cancel</Button>
                  )}
                </AccordionDetails>
              </Accordion>
            ) : (
              <Button
                startIcon={<AddCircleOutlineIcon />}
                sx={{ mt: 2 }}
                onClick={() => setAddingLocation(true)}
              >
                Add Location
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Menu
        open={!!removeTarget}
        anchorEl={removeTarget?.anchor}
        onClose={() => setRemoveTarget(null)}
      >
        <MenuItem onClick={onRemove} disabled={deleteLoading}>
          Confirm Remove
          {deleteLoading ? <CircularProgress size={20} sx={{ ml: 1 }} /> : null}
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default ErrandType;

gql(`
  query ErrandLocations($errandTypeId: Int!)
  @permission(requires: manageOrgConfig)
  {
    organizationalUnit(orderBy: { name: { direction: asc, priority: 1 } }) {
      id
      name
      active
    }
    errandLocation(
      orderBy: {name: { direction: asc, priority: 1 }}
      where: { errandTypeId: { eq: $errandTypeId } }
    ) {
      id
      name
      description
      errandTypeId
      organizationalUnitId
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
      addressTimezone
    }
  }
`);

const DELETE_ERRAND_LOCATION = gql(`
  mutation DeleteErrandLocation($id: Int!)
  @permission(requires: manageOrgConfig)
  {
    deleteErrandLocation(id: $id) {
      id
      deleted
    }
  }
`);
